import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { featuredPortfolio } from "../data/featuredPortfolio";

import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useMotionTemplate,
} from "framer-motion";

import {
  ArrowUpRight,
  Instagram,
  Clock3,
  IndianRupee,
  Gauge,
} from "lucide-react";

import {
  MaskedLines,
  MaskedWords,
  Reveal,
} from "../components/Reveal";

import EditorialMarquee from "../components/EditorialMarquee";
import { JsonLd } from "../components/SEO";
import { api, wordpressApi } from "../lib/api";

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Best Digital Marketing Agency in Amritsar | SEO, Google Ads & Social Media",
  url: "https://dorado9.com/",
  description:
    "Dorado9 is a digital marketing agency in Amritsar offering SEO, Google Ads, social media marketing, web development and creative services.",
  about: {
    "@type": "Organization",
    name: "Dorado9",
    url: "https://dorado9.com/",
  },
};


const services = [
  { id: 1, slug: "seo", title: "Search Engine Optimization", tag: "SEO", desc: "Technical audits, editorial content, and link earning that ships real rank movement.", span: "lg:col-span-7" },
  { id: 2, slug: "paid-ads", title: "Meta & Google Ads", tag: "Paid Media", desc: "Creative testing systems and scaling playbooks with predictable ROAS.", span: "lg:col-span-5" },
  { id: 3, slug: "social-media", title: "Social Media Studio", tag: "Content", desc: "Signature content pillars, hook frameworks, and community building.", span: "lg:col-span-5" },
  { id: 4, slug: "whatsapp-marketing", title: "WhatsApp & Email", tag: "Lifecycle", desc: "Compliant broadcast, cart recovery, and lifecycle flows that print revenue.", span: "lg:col-span-7" },
  { id: 5, slug: "web-and-app-development", title: "Website & App Development", tag: "Build", desc: "Editorial marketing sites, Shopify, and mobile apps tuned for conversion.", span: "lg:col-span-6" },
  { id: 6, slug: "graphic-design", title: "Graphic Design", tag: "Craft", desc: "Brand identity, packaging, and campaign visuals that make people stop.", span: "lg:col-span-6" },
];

const process = [
  { n: "01", title: "Discovery & Goals", body: "We start by understanding your business from the inside out. Your goals, audience, current challenges, positioning, and what growth actually means for you. This gives us a clear direction before we make a single move." },
  { n: "02", title: "Research & Market Analysis", body: "Next, we dig into your market, competitors, search behaviour, customer journey, and existing performance. We look for gaps, opportunities, and patterns that help us understand where your brand can compete and win." },
  { n: "03", title: "Strategy & Planning", body: "Research becomes a focused growth plan. We decide which channels matter, what needs to be communicated, where your budget should go, and what success should look like. Every action has a reason behind it." },
  { n: "04", title: "Implementation & Execution", body: "This is where the plan comes to life. From SEO and paid campaigns to social content, creative, websites, and landing pages, we execute across the channels that make sense for your business while keeping everything connected." },
  { n: "05", title: "Monitoring & Optimization", body: "Launching is only the beginning. We continuously track performance, study what people respond to, test new ideas, and improve what isn’t working. The goal is simple: better decisions, stronger performance, and sustainable growth over time." },
];

const testimonials = [
  {
    quote:
      "I got an app made from them for my business. Just gave them a rough idea but they handled everything from start to finish. They were open to the changes i suggested and understood my requirements very well 👍🏼. The app was done in less than month and it works perfectly. I am collaborating with them on some other projects. Overall highly recommended",
    name: "Vikas",
    role: "Founder, Profitrains",
    image:
      "https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/sxw.png",
  },
  {
    quote:
      "My website was created by dorado9. They delivered exactly what i asked for.... team was professional and constantly in touch. They were open to our suggestions and price was fair too. 😊",
    name: "Simerpreet Kaur",
    role: "CEO, Personagrow",
    image:
      "https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/sxw.png",
  },
  {
    quote:
      "We have had a great experience working with Dorado9. The team is creative, responsive, and always willing to understand our requirements before suggesting a solution. Their work on our digital campaigns has helped us reach more parents and communicate our school’s message effectively. It has been a pleasure working with them.",
    name: "Arti",
    role: "Principal, DBCPS",
    image:
      "https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/sxw.png",
  },
  {
    quote:
      "What I appreciate most about working with Dorado9 is that they genuinely take ownership of the work. I don’t have to constantly follow up or explain things over and over. They understand what we’re trying to achieve, bring their own ideas to the table, and get things done. It’s been a smooth experience and I’m really happy with what we’ve built together.",
    name: "Suraj",
    role: "Owner, Seetesavaye",
    image:
      "https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/sxw.png",
  },
];


// Fake Instagram grid (until user provides handle). Uses real editorial photos.

function TestimonialCard({ testimonial: t, index }) {
  const cardRef = useRef(null);

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);



  };


  return (
    <div
      ref={cardRef}
      onMouseMove={onMove}
      className="
        spotlight-card
        group
        relative
        h-[310px]
        overflow-hidden
        border
        border-white/[0.06]
        bg-white/[0.015]
        p-7
        transition-all
        duration-500
        hover:border-brand-mint/35
        flex
        flex-col
      "
    >

      {/* Number */}
      <span className="
        absolute
        right-6
        top-5
        font-serif-accent
        text-4xl
        text-white/[0.04]
      ">
        0{index + 1}
      </span>


      {/* Person */}
      <div className="flex items-center gap-3">

        <img
          src={t.image}
          alt={t.name}
          loading="lazy"
          className="
            h-12
            w-12
            rounded-full
            object-cover
            border
            border-white/10
            grayscale
            transition-all
            duration-500
            group-hover:grayscale-0
          "
        />

        <div>
          <p className="font-display text-lg uppercase tracking-tight">
            {t.name}
          </p>

          <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            {t.role}
          </p>
        </div>

      </div>


      {/* Quote */}
      <div className="mt-7 flex-1">

        <span className="font-serif-accent text-brand-mint text-4xl leading-none">
          “
        </span>

        <p className="
          mt-2
          text-zinc-300
          text-[17px]
          leading-7
        ">
          {t.quote}
        </p>

      </div>


      {/* Bottom accent */}
      <div className="
        h-px
        w-16
        bg-brand-mint/60
        transition-all
        duration-500
        group-hover:w-full
      " />

    </div>
  );
}

function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
}

export default function Home() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    wordpressApi
      .get("", {
        params: {
          endpoint: "posts",
          categories: 49,
          per_page: 3,
          _embed: true,
        },
      })
      .then((r) => {
        const posts = r.data || [];

        const wordpressCourses = posts.map((post) => ({
          id: String(post.id),
          title: post.title?.rendered || "",
          slug: post.slug || "",
          description: stripHtml(
            post.excerpt?.rendered ||
            post.content?.rendered ||
            ""
          ),
          cover_image:
            post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
          price: post.meta?.course_price || "",
          duration: post.meta?.course_duration || "",
          level: post.meta?.course_level || "",
        }));

        setCourses(wordpressCourses);
      })
      .catch((error) => {
        console.error("Failed to load WordPress courses on homepage:", error);
        setCourses([]);
      });
  }, []);

  const brandVideoRef = useRef(null);
useEffect(() => {
  const video = brandVideoRef.current;
  if (!video) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    },
    {
      threshold: 0.55,
    }
  );
  

  observer.observe(video);

  return () => {
    observer.disconnect();
  };
}, []);
 const [posts, setPosts] = useState([]);

useEffect(() => {
  wordpressApi
    .get("", {
      params: {
        endpoint: "posts",
        context: "edit",
        _embed: true,
        per_page: 4,
        categories_exclude: 49,
      },
    })
    .then((r) => {
      const wordpressPosts = (r.data || []).map((post) => ({
        id: String(post.id),
        title: post.title?.rendered || "",
        slug: post.slug || "",
        excerpt: stripHtml(post.excerpt?.rendered || post.excerpt?.raw || ""),
        content: post.content?.rendered || "",
        cover_image:
          post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
        category: "Insights",
        published_at: post.date || "",
        tags: (post._embedded?.["wp:term"] || [])
          .flat()
          .filter((term) => term.taxonomy === "post_tag")
          .map((term) => term.name),
      }));

      setPosts(wordpressPosts);
    })
    .catch((error) => {
      console.error("Failed to load WordPress blogs on homepage:", error);
      setPosts([]);
    });
}, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  function FloatingTestimonial({ testimonial: t }) {
  return (
    <motion.div
      whileHover={{
        y: -6,
        scale: 1.015,
      }}
      transition={{
        type: "spring",
        stiffness: 180,
        damping: 18,
      }}
      className="group"
    >

      {/* Quote card */}
      <div
        className="
          relative
          rounded-[24px]
          border
          border-white/[0.06]
          bg-[#181818]/90
          backdrop-blur-xl
          p-7
          shadow-[0_25px_80px_rgba(0,0,0,.28)]
          transition-all
          duration-500
          group-hover:border-brand-mint/20
        "
      >

        <span className="absolute right-6 top-4 font-serif-accent text-5xl text-brand-mint/[0.08]">
          “
        </span>

        <p className="relative z-10 text-[16px] leading-7 text-zinc-300">
          {t.quote}
        </p>

      </div>


      {/* Person */}
      <div className="mt-5 flex items-center gap-3">

        <img
          src={t.image}
          alt={t.name}
          className="
            h-12
            w-12
            rounded-full
            object-cover
            grayscale
            border
            border-white/10
            transition-all
            duration-500
            group-hover:grayscale-0
          "
        />

        <div>
          <p className="font-display text-base uppercase tracking-tight text-white">
            {t.name}
          </p>

          <p className="mt-1 text-xs text-zinc-500">
            {t.role}
          </p>
        </div>

      </div>

    </motion.div>
  );
}
const processImgX = useMotionValue(0);
const processImgY = useMotionValue(0);
const processImgRotate = useMotionValue(0);

const onProcessMove = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();

  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const centerX = rect.width / 2;
  const centerY = rect.height / 2;

  const moveX = ((x - centerX) / centerX) * 14;
  const moveY = ((y - centerY) / centerY) * 10;
  const rotate = ((x - centerX) / centerX) * 2;

  processImgX.set(moveX);
  processImgY.set(moveY);
  processImgRotate.set(rotate);
};

const onProcessLeave = () => {
  processImgX.set(0);
  processImgY.set(0);
  processImgRotate.set(0);
};
  // Cursor-tracking purple spotlight (GPU-driven, no React re-renders)
  const mx = useMotionValue(50);
  const my = useMotionValue(38);
  const spotlight = useMotionTemplate`radial-gradient(650px circle at ${mx}% ${my}%, rgba(102,76,221,0.28), transparent 55%)`;
  const onHeroMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 100);
    my.set(((e.clientY - r.top) / r.height) * 100);
  };
const [activeStep, setActiveStep] = useState(-1);
const projects = featuredPortfolio;

useEffect(() => {
  const scriptId = "curator-feed-script";

  if (document.getElementById(scriptId)) {
    return;
  }

  const script = document.createElement("script");

  script.id = scriptId;
  script.src =
    "https://cdn.curator.io/published/e3b4e767-0b71-46ab-abc8-5e4857eb3697.js";
  script.async = true;
  script.charset = "UTF-8";

  document.body.appendChild(script);

  return () => {
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.remove();
    }
  };
}, []);

  return (
    <>
      <JsonLd id="home-page" data={homeSchema} />
      {/* HERO */}
      <section ref={heroRef} onMouseMove={onHeroMove} className="relative min-h-screen w-full overflow-hidden bg-brand-bg pt-32 lg:pt-40 pb-20">
        {/* Parallax abstract image — purple gradient nebula */}
        <motion.div
          style={{ y: heroImgY, scale: heroImgScale }}
          className="absolute inset-0 opacity-60"
        >
          <div className="absolute inset-0 grain" />
          <img
            src="https://images.unsplash.com/photo-1620121692029-d088224ddc74?w=1920&q=60&auto=format&fit=crop"
            alt=""
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover mix-blend-screen"
            data-testid="hero-parallax-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/60 via-brand-bg/40 to-brand-bg" />
        </motion.div>

        {/* Cursor-tracking purple spotlight */}
        <motion.div className="absolute inset-0 pointer-events-none z-[5]" style={{ background: spotlight }} />
<div
  className="absolute -bottom-28 left-1/2 -translate-x-1/2 w-[1400px] h-[350px] rounded-full blur-[140px] opacity-40 pointer-events-none"
  style={{
    background:
      "radial-gradient(circle, rgba(102,76,221,.45), rgba(17,245,186,.15), transparent 70%)",
  }}
/>
        {/* Bottom Blend */}
<div
  className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none z-20"
  style={{
    background:
      "linear-gradient(to bottom, rgba(18,18,18,0) 0%, rgba(18,18,18,0.15) 20%, rgba(18,18,18,0.45) 45%, #121212 100%)",
  }}
/>


        {/* Radial purple glow (dominant) + subtle mint spark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85vw] h-[85vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(102,76,221,0.34), transparent 60%)" }} />
        <div className="absolute -bottom-40 -right-20 w-[45vw] h-[45vw] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(17,245,186,0.10), transparent 65%)" }} />

        <motion.div style={{ y: heroTextY }} className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="eyebrow mb-8 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-brand-purple" />
            <p className="eyebrow m-0">Best Digital Marketing Agency in Amritsar | SEO, Google Ads & Social Media</p>
          </motion.div>

          <h1 className="font-display text-[13vw] sm:text-[10vw] lg:text-[7.2vw] leading-[0.88] tracking-tighter uppercase text-white" data-testid="hero-title">
            <MaskedLines
              lines={["Digital marketing", <>built <span className="font-serif-accent text-brand-mint lowercase">for</span> real</>, "business growth."]}
              stagger={0.15}
              delay={0.35}
            />
          </h1>

         <div className="mt-10 lg:mt-12 max-w-[520px]">
  <motion.p
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 1.1 }}
    className="text-zinc-400 text-[18px] lg:text-[20px] leading-8 lg:leading-9"
  >
    <MaskedWords
      text="We help ambitious brands grow through SEO, paid media, websites and creative strategy. Built in Amritsar. Designed to scale anywhere."
      delay={1.2}
    />
  </motion.p>

  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 1.6 }}
    className="mt-8 flex flex-wrap items-center gap-5"
  >
    <Link
      to="/contact"
      className="btn-primary"
      data-testid="hero-cta-primary"
    >
      Book Consultation
      <ArrowUpRight size={18} />
    </Link>

    <Link
      to="/portfolio"
      className="btn-mint"
      data-testid="hero-cta-secondary">
      View Work
    </Link>
  </motion.div>
</div>

          {/* Bottom credits row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="absolute bottom-6 left-6 lg:left-12 right-6 lg:right-12 flex items-end justify-between text-xs text-zinc-500 uppercase tracking-[0.24em]"
          >
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <EditorialMarquee />

      {/* SERVICES BENTO */}
  {/* SERVICES BENTO */}
<section className="
  relative
  bg-brand-bg
  pt-24 lg:pt-40
  pb-16 lg:pb-20
  px-6 lg:px-12
  border-t border-zinc-900
">
  <div className="max-w-[1600px] mx-auto">

    {/* HEADER */}
    <div className="relative mb-16">

      <Reveal>
        <p className="eyebrow mb-6">
          What We Do
        </p>

        <h2
          className="
            font-display
            text-4xl
            lg:text-7xl
            tracking-tighter
            uppercase
            leading-[0.9]
            max-w-5xl
          "
        >
          EVERYTHING YOUR BRAND
          <br />

          NEEDS TO{" "}

          <span className="font-serif-accent text-brand-mint">
            GROW ONLINE.
          </span>
        </h2>

        <p className="mt-8 max-w-2xl text-zinc-400 text-base lg:text-lg leading-8">
          From SEO and Google Ads to social media, websites, app development and
          creative strategy, we build connected digital solutions that help
          businesses get found, attract the right audience and convert more
          customers.
        </p>
      </Reveal>


      {/* DESKTOP ALL SERVICES */}
      <div className="hidden lg:block absolute right-0 top-16">

        <Reveal delay={0.15}>
          <Link
            to="/services"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            All Services
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>

      </div>


      {/* MOBILE ALL SERVICES */}
      <div className="mt-8 lg:hidden">

        <Reveal delay={0.15}>
          <Link
            to="/services"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            All Services
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>

      </div>

    </div>


    {/* SERVICE CAROUSEL */}
    <div
      data-lenis-prevent
      className="
        mt-20
        flex
        gap-4
        overflow-x-auto
        snap-x
        snap-mandatory
        overscroll-x-contain
        -mr-6
        pb-6
        pr-20
        lg:gap-6
        lg:mr-0
        lg:pr-0
        scrollbar-hide
      "
    >
      {services.map((s) => (
        <div
          key={s.id}
          className="
            shrink-0
            snap-start
            w-[calc(100vw-5.5rem)]
            sm:w-[320px]
            lg:w-[360px]
          "
        >
          <ServiceCard s={s} />
        </div>

      ))}
    </div>

  </div>

</section>
{/* BRAND VIDEO */}
<section className="relative bg-brand-bg py-16 lg:py-24 px-6 lg:px-12 overflow-hidden">

  {/* Purple ambient glow */}
  <div
    className="
      absolute
      left-1/2 top-1/2
      -translate-x-1/2 -translate-y-1/2
      w-[700px] h-[450px]
      rounded-full
      blur-[160px]
      opacity-[0.10]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.9), transparent 68%)",
    }}
  />

  <div className="relative z-10 max-w-[1080px] mx-auto">

    {/* FILM LABELS */}
    <Reveal>
      <div className="
        mb-6
        flex
        items-center
        justify-between
        border-b
        border-white/[0.07]
        pb-5
      ">

        <div className="flex items-center gap-3">

          <span className="
            w-2 h-2
            rounded-full
            bg-brand-purple
          " />

          <Link
            to="/watch/dorado9-film-001"
            className="
            text-[10px]
            uppercase
            tracking-[0.3em]
            text-brand-mint
            hover:text-white
            transition-colors
          "
          >
            Dorado9 Film 001
          </Link>

        </div>

        <p className="
          hidden sm:block
          text-[10px]
          uppercase
          tracking-[0.3em]
          text-brand-mint
        ">
          Make them look twice
        </p>

      </div>
    </Reveal>


    {/* 16:9 VIDEO */}
    <Reveal delay={0.1}>
      <div className="
  relative
  w-full
  aspect-video
  overflow-hidden
  rounded-[24px]
  bg-[#171717]
  border
  border-white/[0.07]
">

  <video
    ref={brandVideoRef}
    className="absolute inset-0 w-full h-full object-cover"
    muted
    playsInline
    controls
    preload="metadata"
  >
    <source
      src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/Comp-3.mp4"
      type="video/mp4"
    />
  </video>

  {/* CLICK AREA FOR MUTE / UNMUTE */}
  <button
    type="button"
    aria-label="Toggle video sound"
    onClick={() => {
      const video = brandVideoRef.current;
      if (!video) return;

      video.muted = !video.muted;

      if (!video.paused) {
        video.play().catch(() => {});
      }
    }}
    className="
      absolute
      left-0
      right-0
      top-0
      bottom-[52px]
      z-10
      cursor-pointer
      bg-transparent
    "
  />
</div>
    </Reveal>


    {/* BOTTOM LABEL */}
    <Reveal delay={0.15}>
      <div className="
        mt-5
        flex
        items-center
        justify-between
        gap-5
      ">

        <Link
          to="/watch/dorado9-film-001"
          className="
          text-[10px]
          uppercase
          tracking-[0.25em]
          text-brand-mint
          hover:text-white
          transition-colors
        "
        >
          Watch film ↗
        </Link>

        <ArrowUpRight
          size={15}
          className="text-brand-purple"
        />

      </div>
    </Reveal>

  </div>

</section>

      {/* MANIFESTO / PROCESS */}
<section
  onMouseMove={onProcessMove}
  onMouseLeave={onProcessLeave}
  className="
    relative
    overflow-hidden
    bg-brand-bg
    py-24
    lg:py-30
    px-6
    lg:px-12
  "
>
  {/* subtle section atmosphere */}
  <div
    className="
      absolute
      -left-40
      bottom-0
      h-[600px]
      w-[600px]
      rounded-full
      blur-[180px]
      opacity-[0.10]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.8), transparent 68%)",
    }}
  />

  <div className="relative z-10 max-w-[1450px] mx-auto grid gap-12 lg:grid-cols-12 lg:gap-16">

    {/* LEFT */}
    <div className="relative lg:col-span-5 lg:min-h-[760px]">

      <Reveal>
        <p className="eyebrow mb-6">
          Our Way of Doing Things
        </p>

        <h2
  className="
    font-display
    text-[2.25rem]
    sm:text-5xl
    lg:text-6xl
    tracking-tighter
    uppercase
    leading-[0.9]
    max-w-4xl
  "
>
  <span>
    FROM STRATEGY TO
  </span>

  <span className="block font-serif-accent text-brand-mint">
    measurable growth.
  </span>
</h2>

        <p className="mt-4 text-zinc-400 text-base max-w-sm leading-7">
          We work with brands to build strategies that actually make sense.
          From planning to posting to tracking, we handle it all.
        </p>
      </Reveal>

{/* RECTANGULAR CTA CARD */}
<div className="mt-10 lg:mt-12">

  <div
    className="
      group
      relative
      h-[170px]
      sm:h-[180px]
      lg:h-[190px]
      w-full
      overflow-visible
      rounded-[26px]
      bg-transparent
      p-7
      pl-0
      sm:p-9
      sm:pl-0
      lg:p-10
      lg:pl-0
    "
  >

    {/* TEXT */}
    <div
      className="
        relative
        z-10
        flex
        h-full
        items-start
        text-left
      "
    >
      <h3
        className="
          font-display
          text-[2rem]
          sm:text-[2.6rem]
          lg:text-[3rem]
          uppercase
          leading-[0.9]
          tracking-tighter
          text-white
          whitespace-nowrap
        "
      >
        LET'S TALK
        <br />

        <span className="font-serif-accent lowercase text-brand-mint">
          growth.
        </span>
      </h3>
    </div>


    {/* CIRCULAR CTA */}
    <a
      href="https://wa.me/919356020762"
      target="_blank"
      rel="noreferrer"
      aria-label="Let's talk"
      className="
        group/cta
        absolute
        right-6
        bottom-6
        z-20
        flex
        h-[120px]
        w-[120px]
        items-center
        justify-center
        rounded-full

        sm:right-7
        sm:bottom-7
        sm:h-[130px]
        sm:w-[130px]

        lg:right-8
        lg:bottom-8
        lg:h-[140px]
        lg:w-[140px]

        transition-all
        duration-500
      "
    >

      {/* NEON PURPLE GLOW
          Uses circular box-shadows instead of a blurred rectangular element */}
      <div
        className="
          pointer-events-none
          absolute
          inset-0
          rounded-full
          bg-brand-purple
          shadow-[0_0_14px_rgba(102,76,221,0.65),0_0_32px_rgba(102,76,221,0.55),0_0_64px_rgba(102,76,221,0.4),0_0_110px_rgba(102,76,221,0.22)]
          transition-all
          duration-500
          group-hover/cta:shadow-[0_0_18px_rgba(102,76,221,0.75),0_0_40px_rgba(102,76,221,0.65),0_0_75px_rgba(102,76,221,0.5),0_0_120px_rgba(102,76,221,0.3)]
          group-hover/cta:scale-[1.04]
        "
      />


      {/* ROTATING TEXT */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
        className="
          pointer-events-none
          absolute
          inset-0
          z-10
        "
      >
        <svg
          viewBox="0 0 200 200"
          className="h-full w-full"
        >
          <defs>
            <path
              id="ctaCircle"
              d="
                M100,100
                m-78,0
                a78,78 0 1,1 156,0
                a78,78 0 1,1 -156,0
              "
            />
          </defs>

          <text
            fill="#111111"
            style={{
              fontSize: "12px",
              letterSpacing: "4.2px",
              fontWeight: 900,
            }}
          >
            <textPath
              href="#ctaCircle"
              startOffset="0%"
            >
              CALL NOW • LET'S TALK GROWTH • BOOK A CALL •
            </textPath>
          </text>
        </svg>
      </motion.div>


      {/* BLACK CENTER */}
      <div
        className="
          relative
          z-20
          flex
          h-[71px]
          w-[71px]
          items-center
          justify-center
          rounded-full
          bg-[#171717]
          transition-transform
          duration-500
          group-hover/cta:scale-[1.06]

          sm:h-[78px]
          sm:w-[78px]

          lg:h-[85px]
          lg:w-[85px]
        "
      >
        <ArrowUpRight
          size={40}
          strokeWidth={4.8}
          className="
            text-white
            transition-transform
            duration-500
            group-hover/cta:translate-x-1
            group-hover/cta:-translate-y-1
          "
        />
      </div>

    </a>

  </div>

</div>

    </div>

    {/* RIGHT */}
    <div className="lg:col-span-7">

      {process.map((p, i) => {
        const active = activeStep === i;

        return (
          <Reveal key={p.n} delay={i * 0.05}>

            <div
              onClick={() => setActiveStep(active ? -1 : i)}
              className={`
                group
                cursor-pointer
                border-b
                transition-all
                duration-500

                 ${

    active

      ? "border-brand-mint/35"

      : "border-zinc-900 hover:border-brand-mint/25"

  }
              `}
            >

              <div
                className="
                  grid
                  grid-cols-[58px_minmax(0,1fr)_28px]
                  lg:grid-cols-[90px_1fr_40px]
                  gap-3
                  sm:gap-4
                  lg:gap-6
                  py-6
                  lg:py-8
                  items-start
                "
              >

                {/* Number */}
                <span
                  className={`
                    font-serif-accent
                    text-5xl
                    lg:text-7xl
                    text-brand-mint
                    leading-none
                    transition-transform
                    duration-500

                    ${
                      active
                        ? "-translate-y-1"
                        : "group-hover:-translate-x-1"
                    }
                  `}
                >
                  {p.n}
                </span>


                {/* Content */}
                <div className="min-w-0">

                  <h3
                    className={`
                      font-display
                      text-xl
                      sm:text-2xl
                      lg:text-4xl
                      uppercase
                      tracking-tight
                      transition-transform
                      duration-500

                      ${
                        active
                          ? "translate-x-2"
                          : "group-hover:translate-x-1"
                      }
                    `}
                  >
                    {p.title}
                  </h3>


                  <div
                    className={`
                      overflow-hidden
                      transition-all
                      duration-500

                      ${
                        active
                          ? "max-h-40 opacity-100 mt-5"
                          : "max-h-0 opacity-0"
                      }
                    `}
                  >

                    <div className="h-px w-14 bg-brand-mint mb-5" />

                    <p className="text-zinc-400 max-w-xl leading-8">
                      {p.body}
                    </p>

                  </div>

                </div>


                {/* Plus */}
                <div className="flex justify-end pt-2">

                  <span
                    className={`
                      text-3xl
                      font-extralight
                      leading-none
                      transition-all
                      duration-300

                      ${
                        active
                          ? "rotate-45 text-brand-mint"
                          : "text-zinc-600 group-hover:text-brand-mint"
                      }
                    `}
                  >
                    +
                  </span>

                </div>

              </div>

            </div>

          </Reveal>
        );
      })}

    </div>

  </div>
</section>





      {/* PORTFOLIO PREVIEW */}
<section className="
  relative
  bg-brand-bg
  pt-16 lg:pt-20
  pb-24 lg:pb-40
  px-6 lg:px-12
  border-t border-zinc-900
">

  <div className="max-w-[1600px] mx-auto">

    {/* HEADER */}
    <div className="relative mb-16">

      <Reveal>
        <p className="eyebrow mb-6">
          Selected Work
        </p>

        <h2 className="
          font-display
          text-4xl
          lg:text-7xl
          tracking-tighter
          uppercase
          leading-[0.9]
          max-w-4xl
        ">
          WORK WE'RE PROUD TO
          <br />

          <span className="font-serif-accent text-brand-mint">
            PUT OUR NAME ON.
          </span>
        </h2>

        <p className="mt-8 max-w-2xl text-zinc-400 text-base lg:text-lg leading-8">
          A selection of websites, campaigns, branding and digital experiences
          built to solve real business problems and create work that performs
          as well as it looks.
        </p>
      </Reveal>


      {/* DESKTOP VIEW ARCHIVE */}
      <div className="hidden lg:block absolute right-0 top-16">

        <Reveal delay={0.15}>
          <Link
            to="/portfolio"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            View Archive
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>

      </div>


      {/* MOBILE VIEW ARCHIVE */}
      <div className="mt-8 lg:hidden">

        <Reveal delay={0.15}>
          <Link
            to="/portfolio"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            View Archive
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>

      </div>

    </div>


    {/* PROJECT GRID */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">

      {projects.map((p, i) => (

        <Reveal
          key={p.id}
          delay={i * 0.1}
        >

          <Link
            to={`/portfolio/${p.slug}`}
            data-testid={`portfolio-card-${p.slug}`}
            className="group block"
          >

            <div className="
              spotlight-card
              aspect-[4/3]
              overflow-hidden
              border
              border-zinc-900
            ">
              <img
                src={p.cover_image}
                alt={p.title}
                className="
                  w-full
                  h-full
                  object-cover
                  transition-transform
                  duration-[1200ms]
                  ease-out
                  group-hover:scale-105
                  grayscale
                  group-hover:grayscale-0
                "
              />
            </div>


            <div className="mt-5 flex items-start justify-between gap-6">

              <div>

                <p className="
                  text-xs
                  text-brand-mint
                  uppercase
                  tracking-[0.24em]
                ">
                  {p.category}
                </p>

                <h3 className="
                  font-display
                  text-2xl
                  lg:text-3xl
                  mt-2
                  tracking-tight
                ">
                  {p.title}
                </h3>

              </div>


              {p.year && (
                <span className="
                  font-serif-accent
                  text-brand-mint
                  text-lg
                  opacity-70
                  shrink-0
                ">
                  {p.year}
                </span>
              )}

            </div>

          </Link>

        </Reveal>

      ))}

    </div>

  </div>

</section>
{/* COURSES PREVIEW */}
<section className="relative bg-brand-bg py-20 lg:py-28 px-6 lg:px-12 border-t border-zinc-900 overflow-hidden">

  <div className="max-w-[1600px] mx-auto">

    {/* HEADER */}
    <div className="relative mb-16">

      <Reveal>
        <p className="eyebrow mb-6">
          Learn With Dorado9
        </p>

        <h2 className="
          font-display
          text-4xl
          lg:text-6xl
          tracking-tighter
          uppercase
          leading-[0.95]
          max-w-4xl
        ">
          PRACTICAL SKILLS FOR{" "}
          <span className="font-serif-accent text-brand-mint lowercase">
            {" "}real-world work.
          </span>
        </h2>

        <p className="
          mt-7
          max-w-2xl
          text-zinc-400
          text-base
          lg:text-lg
          leading-8
        ">
          Hands-on courses built around real tools, practical workflows and
          projects that help you build skills you can actually use.
        </p>
      </Reveal>

      {/* DESKTOP LINK */}
      <div className="hidden lg:block absolute right-0 top-14">
        <Reveal delay={0.15}>
          <Link
            to="/courses"
            className="
              inline-flex
              items-center
              gap-2
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            View all courses
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>

      {/* MOBILE LINK */}
      <div className="mt-8 lg:hidden">
        <Reveal delay={0.15}>
          <Link
            to="/courses"
            className="
              inline-flex
              items-center
              gap-2
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
          >
            View all courses
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>

    </div>


    {/* COURSES */}
    <div className="space-y-8">

      {courses.map((course, i) => (

        <Reveal
          key={course.id || course.slug}
          delay={i * 0.08}
        >

          <Link
            to={`/courses/${course.slug}`}
            className="
              group
              block
              overflow-hidden
              rounded-[28px]
              border
              border-white/[0.06]
              bg-white/[0.015]
              transition-all
              duration-500
              hover:border-brand-mint/20
            "
          >

            <div className="grid lg:grid-cols-2 min-h-[430px]">


              {/* IMAGE */}
              <div className="
                relative
                overflow-hidden
                min-h-[300px]
                lg:min-h-full
              ">

                <img
                  src={
                    course.cover_image ||
                    course.image ||
                    course.thumbnail ||
                    course.featured_image
                  }
                  alt={course.title}
                  loading="lazy"
                  className="
                    absolute
                    inset-0
                    w-full
                    h-full
                    object-cover
                    transition-transform
                    duration-700
                    ease-out
                    group-hover:scale-[1.03]
                  "
                />

                {/* subtle image overlay */}
                <div className="
                  absolute
                  inset-0
                  bg-gradient-to-r
                  from-transparent
                  to-black/10
                  pointer-events-none
                " />

              </div>


              {/* CONTENT */}
              <div className="
                flex
                flex-col
                justify-center
                p-8
                sm:p-10
                lg:p-12
                xl:p-14
              ">

                {/* CATEGORY */}
                <p className="
                  text-[10px]
                  uppercase
                  tracking-[0.28em]
                  text-brand-mint
                ">
                  {course.category || course.tag || "Course"}
                </p>


                {/* TITLE */}
                <h3 className="
                  mt-5
                  font-display
                  text-4xl
                  lg:text-5xl
                  uppercase
                  tracking-tight
                  leading-[0.95]
                  text-white
                  transition-colors
                  duration-300
                  group-hover:text-brand-mint
                ">
                  {course.title}
                </h3>


                {/* DESCRIPTION */}
                <p className="
                  mt-6
                  max-w-xl
                  text-zinc-400
                  text-base
                  lg:text-lg
                  leading-8
                ">
                  {course.description ||
                    course.desc ||
                    course.excerpt ||
                    course.short_description}
                </p>


                {/* DETAILS */}
                <div className="
                  mt-9
                  pt-6
                  border-t
                  border-white/[0.08]
                  grid
                  grid-cols-3
                  gap-6
                ">


                  {/* PRICE */}
                  <div className="flex flex-col gap-2">

                    <IndianRupee
                      size={19}
                      strokeWidth={1.7}
                      className="text-brand-purple"
                    />

                    <p className="
                      text-[9px]
                      uppercase
                      tracking-[0.2em]
                      text-zinc-600
                    ">
                      Fee
                    </p>

                    <span className="
                      text-xs
                      uppercase
                      tracking-[0.12em]
                      text-zinc-300
                    ">
                      {course.price ||
                        course.fee ||
                        "Contact Us"}
                    </span>

                  </div>


                  {/* DURATION */}
                  <div className="flex flex-col gap-2">

                    <Clock3
                      size={19}
                      strokeWidth={1.7}
                      className="text-brand-purple"
                    />

                    <p className="
                      text-[9px]
                      uppercase
                      tracking-[0.2em]
                      text-zinc-600
                    ">
                      Duration
                    </p>

                    <span className="
                      text-xs
                      uppercase
                      tracking-[0.12em]
                      text-zinc-300
                    ">
                      {course.duration || "Flexible"}
                    </span>

                  </div>


                  {/* LEVEL */}
                  <div className="flex flex-col gap-2">

                    <Gauge
                      size={19}
                      strokeWidth={1.7}
                      className="text-brand-purple"
                    />

                    <p className="
                      text-[9px]
                      uppercase
                      tracking-[0.2em]
                      text-zinc-600
                    ">
                      Level
                    </p>

                    <span className="
                      text-xs
                      uppercase
                      tracking-[0.12em]
                      text-zinc-300
                    ">
                      {course.level ||
                        course.difficulty ||
                        "Beginner"}
                    </span>

                  </div>

                </div>


                {/* CTA */}
                <div className="
                  mt-9
                  inline-flex
                  items-center
                  gap-3
                  text-sm
                  uppercase
                  tracking-[0.22em]
                  text-white
                  group-hover:text-brand-mint
                  transition-colors
                ">
                  Explore Course

                  <ArrowUpRight
                    size={17}
                    className="
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                      group-hover:-translate-y-1
                    "
                  />
                </div>

              </div>

            </div>

          </Link>

        </Reveal>

      ))}

    </div>

  </div>

</section>

      {/* INSTAGRAM FEED */}
<section
  className="
    relative
    bg-brand-bg
    pt-24
    lg:pt-32
    pb-24
    lg:pb-32
    px-6
    lg:px-12
    border-t
    border-zinc-900
  "
  data-testid="instagram-section"
>
  <div className="max-w-[1600px] mx-auto">

    <div className="relative mb-16">

      <Reveal>
        <p className="eyebrow mb-6 flex items-center gap-3">
          <Instagram size={14} />
          Fresh from Instagram
        </p>

        <h2
          className="
            font-display
            text-4xl
            lg:text-6xl
            tracking-tighter
            uppercase
            leading-[0.95]
            max-w-3xl
          "
        >
              WHAT WE’VE {" "}
          <span className="font-serif-accent text-brand-mint">
            BEEN UP TO.
          </span>
        </h2>

        <p className="
          mt-8
          max-w-2xl
          text-zinc-400
          text-base
          lg:text-lg
          leading-8
        ">
          A closer look at the campaigns, creative work, ideas and behind-the-scenes
          moments shaping what we build every day.
        </p>
      </Reveal>

      <div className="hidden lg:block absolute right-0 top-16">
        <Reveal delay={0.15}>
          <a
            href="https://instagram.com/the.dorado9"
            target="_blank"
            rel="noreferrer"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
            data-testid="instagram-follow"
          >
            Follow @the.dorado9
            <ArrowUpRight size={15} />
          </a>
        </Reveal>
      </div>

      <div className="mt-8 lg:hidden">
        <Reveal delay={0.15}>
          <a
            href="https://instagram.com/the.dorado9"
            target="_blank"
            rel="noreferrer"
            className="
              inline-flex
              items-center
              gap-2
              whitespace-nowrap
              uppercase
              tracking-[0.24em]
              text-sm
              text-zinc-300
              link-underline
            "
            data-testid="instagram-follow-mobile"
          >
            Follow @the.dorado9
            <ArrowUpRight size={15} />
          </a>
        </Reveal>
      </div>

    </div>

    <div
      id="curator-feed-default-feed-layout"
      className="w-full"
      style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <a
        href="https://curator.io"
        target="_blank"
        rel="noreferrer"
        className="crt-logo crt-tag"
      >
        Powered by Curator.io
      </a>
    </div>

  </div>

</section>

      {/* TESTIMONIALS */}
<section className="relative overflow-hidden bg-brand-bg py-24 lg:py-40 px-6 lg:px-12 border-t border-zinc-900">

  {/* Purple atmosphere */}
  <div
    className="
      absolute
      left-1/2
      top-1/2
      -translate-x-1/2
      -translate-y-1/2
      w-[900px]
      h-[700px]
      rounded-full
      blur-[190px]
      opacity-[0.12]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,0.8) 0%, rgba(102,76,221,0.22) 35%, transparent 72%)",
    }}
  />

  {/* Tiny mint atmosphere */}
  <div
    className="
      absolute
      left-[10%]
      bottom-[5%]
      w-[360px]
      h-[360px]
      rounded-full
      blur-[170px]
      opacity-[0.035]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(17,245,186,0.8), transparent 70%)",
    }}
  />

  {/* Dotted texture */}
  <div
    className="
      absolute
      left-1/2
      top-1/2
      h-[850px]
      w-[1100px]
      -translate-x-1/2
      -translate-y-1/2
      opacity-[0.09]
      pointer-events-none
    "
    style={{
      backgroundImage:
        "radial-gradient(circle, rgba(255,255,255,.45) 1px, transparent 1px)",
      backgroundSize: "12px 12px",
      maskImage:
        "radial-gradient(ellipse at center, black 20%, transparent 72%)",
      WebkitMaskImage:
        "radial-gradient(ellipse at center, black 20%, transparent 72%)",
      transform: "translate(-50%, -50%) rotate(-8deg)",
    }}
  />

  <div className="relative z-10 max-w-[1450px] mx-auto">

    {/* ================= MOBILE ================= */}
    {/* ================= DESKTOP ================= */}
<div className="hidden lg:block relative min-h-[980px]">

  {/* Center heading */}
  <div
    className="
      absolute
      left-1/2
      top-1/2
      w-full
      max-w-[680px]
      -translate-x-1/2
      -translate-y-1/2
      text-center
    "
  >
    <Reveal>
      <h2
        className="
          mt-8
          font-display
          text-5xl
          lg:text-7xl
          tracking-tighter
          leading-[0.9]
          text-white
          uppercase
        "
      >
        HEAR FROM THE{" "}
        <span className="font-serif-accent text-brand-mint uppercase">
          CLIENTS
        </span>{" "}
        WE'VE PARTNERED WITH.
      </h2>
    </Reveal>
  </div>


  {/* TOP LEFT */}
  <motion.div
    initial={{ opacity: 0, y: 45, scale: 0.96 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.35 }}
    transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
    }}
    className="absolute left-[5%] top-[2%] w-[465px]"
  >
    <FloatingTestimonial testimonial={testimonials[0]} />
  </motion.div>


  {/* TOP RIGHT */}
  <motion.div
    initial={{ opacity: 0, y: 45, scale: 0.96 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.35 }}
    transition={{
      duration: 0.7,
      delay: 0.12,
      ease: [0.16, 1, 0.3, 1],
    }}
    className="absolute right-[4%] top-[10%] w-[410px]"
  >
    <FloatingTestimonial testimonial={testimonials[1]} />
  </motion.div>


  {/* BOTTOM LEFT */}
  <motion.div
    initial={{ opacity: 0, y: 45, scale: 0.96 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.35 }}
    transition={{
      duration: 0.7,
      delay: 0.22,
      ease: [0.16, 1, 0.3, 1],
    }}
    className="absolute left-[3%] top-[67%] w-[460px]"
  >
    <FloatingTestimonial testimonial={testimonials[2]} />
  </motion.div>


  {/* BOTTOM RIGHT */}
  <motion.div
    initial={{ opacity: 0, y: 45, scale: 0.96 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.35 }}
    transition={{
      duration: 0.7,
      delay: 0.32,
      ease: [0.16, 1, 0.3, 1],
    }}
    className="absolute right-[4%] top-[75%] w-[510px]"
  >
    <FloatingTestimonial testimonial={testimonials[3]} />
  </motion.div>

</div>

  </div>

</section>
{/* BLOG PREVIEW */}
<section className="relative bg-brand-bg py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">

  {/* subtle glow */}
  <div
    className="
      absolute
      -right-32
      top-1/3
      w-[520px]
      h-[520px]
      rounded-full
      blur-[170px]
      opacity-[0.08]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.85), transparent 70%)",
    }}
  />

  <div className="relative z-10 max-w-[1600px] mx-auto">

    {/* HEADER */}
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">

      <Reveal>
        <p className="eyebrow mb-6">
          From the Dorado9 Desk
        </p>

        <h2 className="
          font-display
          text-4xl
          lg:text-6xl
          uppercase
          tracking-tighter
          leading-[0.95]
          max-w-3xl
        ">
          IDEAS, BREAKDOWNS &{" "}
          <span className="font-serif-accent text-brand-mint lowercase">
            things worth reading.
          </span>
        </h2>

        <p className="
          mt-7
          max-w-2xl
          text-zinc-400
          text-base
          lg:text-lg
          leading-8
        ">
          Practical thoughts on SEO, paid media, websites, creative strategy
          and the things we learn while building for real businesses.
        </p>
      </Reveal>


      <Reveal delay={0.15}>
        <Link
          to="/blog"
          className="
            inline-flex
            items-center
            gap-2
            uppercase
            tracking-[0.24em]
            text-sm
            text-zinc-300
            link-underline
          "
        >
          View all articles
          <ArrowUpRight size={15} />
        </Link>
      </Reveal>

    </div>


    {/* BLOG LAYOUT */}
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">

      {/* FEATURED ARTICLE */}
      <div className="lg:col-span-7">

        {posts[0] && (
          <Reveal>
            <Link
              to={`/blog/${posts[0].slug}`}
              className="group block"
            >

              <div className="
                relative
                aspect-[16/10]
                overflow-hidden
                border
                border-white/[0.06]
                bg-white/[0.015]
              ">

                <img
                  src={posts[0].cover_image}
                  alt={posts[0].title}
                  className="
                    w-full
                    h-full
                    object-cover
                    grayscale
                    transition-all
                    duration-[1000ms]
                    group-hover:grayscale-0
                    group-hover:scale-[1.04]
                  "
                />

                <div className="
                  absolute
                  inset-0
                  bg-gradient-to-t
                  from-black/50
                  via-transparent
                  to-transparent
                " />

              </div>


              <p className="
                mt-5
                text-[10px]
                uppercase
                tracking-[0.24em]
                text-brand-mint
              ">
                Featured Article
              </p>

              <h3 className="
                mt-3
                font-display
                text-3xl
                lg:text-5xl
                uppercase
                tracking-tight
                leading-[0.95]
                group-hover:text-brand-mint
                transition-colors
              ">
                {posts[0].title}
              </h3>

              {posts[0].excerpt && (
                <p className="
                  mt-5
                  max-w-2xl
                  text-zinc-400
                  leading-7
                ">
                  {posts[0].excerpt}
                </p>
              )}

            </Link>
          </Reveal>
        )}

      </div>


      {/* RECENT ARTICLES */}
      <div className="lg:col-span-5">



          {posts.slice(1, 4).map((post, i) => (

            <Reveal
              key={post.id}
              delay={i * 0.08}
            >
              <Link
                to={`/blog/${post.slug}`}
                className="
                  group
                  grid
                  grid-cols-[110px_1fr]
                  gap-5
                  py-7
                  first:pt-0
                  items-center
                "
              >

                {/* THUMB */}
                <div className="
                  aspect-square
                  overflow-hidden
                  border
                  border-white/[0.06]
                  bg-white/[0.02]
                ">
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    className="
                      w-full
                      h-full
                      object-cover
                      grayscale
                      transition-all
                      duration-700
                      group-hover:grayscale-0
                      group-hover:scale-105
                    "
                  />
                </div>


                {/* TEXT */}
                <div>
                  <p className="
                    text-[9px]
                    uppercase
                    tracking-[0.22em]
                    text-brand-mint
                    mb-2
                  ">
                    {post.category || "Insights"}
                  </p>

                  <h3 className="
                    font-display
                    text-xl
                    lg:text-2xl
                    uppercase
                    tracking-tight
                    leading-tight
                    text-white
                    group-hover:text-brand-mint
                    transition-colors
                  ">
                    {post.title}
                  </h3>

                  <span className="
                    mt-3
                    inline-flex
                    items-center
                    gap-2
                    text-xs
                    uppercase
                    tracking-[0.18em]
                    text-zinc-500
                    group-hover:text-brand-mint
                    transition-colors
                  ">
                    Read article
                    <ArrowUpRight size={13} />
                  </span>

                </div>

              </Link>
            </Reveal>

          ))}

        </div>

      </div>

    </div>


</section>
{/* CONTACT CTA */}
<section className="relative overflow-hidden bg-brand-bg pt-24 lg:pt-28 pb-16 lg:pb-20 px-6 lg:px-12 border-t border-zinc-900">

  {/* Purple atmosphere */}
  <div
    className="
      absolute
      -left-40
      top-1/2
      -translate-y-1/2
      w-[700px]
      h-[700px]
      rounded-full
      blur-[180px]
      opacity-20
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.75), transparent 68%)",
    }}
  />

  <div
    className="
      absolute
      left-[35%]
      bottom-[-220px]
      w-[500px]
      h-[500px]
      rounded-full
      blur-[170px]
      opacity-[0.08]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(17,245,186,.8), transparent 68%)",
    }}
  />

  <div className="relative z-10 max-w-[1500px] mx-auto">

    <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-center">

      {/* LEFT */}
      <div className="lg:col-span-7">

        <Reveal>
          <p className="eyebrow mb-7">
            Start a Project
          </p>

          <h2
            className="
              font-display
              text-5xl
              sm:text-6xl
              lg:text-[6vw]
              leading-[0.88]
              tracking-tighter
              uppercase
              max-w-4xl
              text-white
            "
          >
            Ready to grow?
            <br />

            <span className="font-serif-accent text-brand-mint lowercase">
              let's build something.
            </span>
          </h2>

          <p
            className="
              mt-9
              max-w-xl
              text-zinc-400
              text-base
              lg:text-lg
              leading-8
            "
          >
            Tell us what you're working on, what you want to improve, or where
            you want to go next. We'll help you find the right way forward.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap gap-x-16 gap-y-7">

            <div>
              <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-600">
                Email
              </p>

              <a
                href="mailto:hello@dorado9.com"
                className="mt-2 inline-block text-zinc-200 hover:text-brand-mint transition-colors"
              >
                hello@dorado9.com
              </a>
            </div>

            <div>
              <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-600">
                Call
              </p>

              <a
                href="tel:+919855844729"
                className="mt-2 inline-block text-zinc-200 hover:text-brand-mint transition-colors"
              >
                +91 9855 844 729
              </a>
            </div>

          </div>
        </Reveal>

      </div>


      {/* RIGHT FORM */}
      <div className="lg:col-span-5">

        <Reveal delay={0.1}>
          <div
            className="
              relative
              overflow-hidden
              rounded-[28px]
              border
              border-white/[0.07]
              bg-white/[0.025]
              backdrop-blur-xl
              p-7
              sm:p-8
              lg:p-10
              shadow-[0_30px_100px_rgba(102,76,221,0.10)]
            "
          >

            {/* Purple form glow */}
            <div
              className="
                absolute
                -right-24
                -top-24
                w-[280px]
                h-[280px]
                rounded-full
                blur-[110px]
                opacity-20
                pointer-events-none
              "
              style={{
                background:
                  "radial-gradient(circle, rgba(102,76,221,.9), transparent 68%)",
              }}
            />

            <div className="relative z-10">

              <p className="text-[10px] uppercase tracking-[0.3em] text-brand-mint mb-8">
                Quick Enquiry
              </p>

              <form className="space-y-6">

                <input
                  type="text"
                  placeholder="Your name"
                  className="
                    w-full
                    bg-transparent
                    border-b
                    border-zinc-800
                    py-4
                    text-white
                    outline-none
                    transition-colors
                    focus:border-brand-mint
                    placeholder:text-zinc-600
                  "
                />

                <input
                  type="email"
                  placeholder="Email address"
                  className="
                    w-full
                    bg-transparent
                    border-b
                    border-zinc-800
                    py-4
                    text-white
                    outline-none
                    transition-colors
                    focus:border-brand-mint
                    placeholder:text-zinc-600
                  "
                />

                <select
                  defaultValue=""
                  className="
                    w-full
                    bg-transparent
                    border-b
                    border-zinc-800
                    py-4
                    text-zinc-400
                    outline-none
                    transition-colors
                    focus:border-brand-mint
                  "
                >
                  <option value="" disabled>
                    Select a service
                  </option>

                  <option value="seo">SEO</option>
                  <option value="ads">Meta & Google Ads</option>
                  <option value="social">Social Media</option>
                  <option value="development">Website / App Development</option>
                  <option value="design">Graphic Design</option>
                  <option value="other">Something Else</option>
                </select>

                <textarea
                  rows="3"
                  placeholder="Tell us about your project..."
                  className="
                    w-full
                    resize-none
                    bg-transparent
                    border-b
                    border-zinc-800
                    py-4
                    text-white
                    outline-none
                    transition-colors
                    focus:border-brand-mint
                    placeholder:text-zinc-600
                  "
                />

                <button
                  type="submit"
                  className="
                    group
                    mt-2
                    inline-flex
                    items-center
                    gap-3
                    rounded-full
                    bg-brand-purple
                    px-7
                    py-4
                    font-semibold
                    text-white
                    transition-all
                    duration-300
                    hover:bg-brand-mint
                    hover:text-black
                  "
                >
                  Send enquiry

                  <ArrowUpRight
                    size={17}
                    className="
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                      group-hover:-translate-y-1
                    "
                  />
                </button>

              </form>

            </div>

          </div>
        </Reveal>

      </div>

    </div>

  </div>
</section>
    </>
  );
}

function ServiceCard({ s }) {
  const cardRef = useRef(null);
  const onMove = (e) => {
    const el = cardRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <Link to={`/services/${s.slug}`} ref={cardRef} onMouseMove={onMove} className="
spotlight-card
group
p-10
lg:p-12
border
border-brand-mint/25
hover:border-brand-mint/50
transition-all
duration-500
h-[340px]
flex
flex-col
justify-between
bg-white/[0.015]
"data-testid={`service-${s.tag.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="relative z-10">
        <p className="text-xs text-brand-mint uppercase tracking-[0.24em]">{s.tag}</p>
        <h3 className="font-display text-2xl lg:text-4xl mt-4 tracking-tight uppercase group-hover:text-brand-mint transition-colors duration-500">{s.title}</h3>
      </div>
      <div className="relative z-10 mt-8 flex items-end justify-between gap-6">
        <p className="text-zinc-400 text-sm lg:text-base max-w-md">{s.desc}</p>
        <span className="text-brand-mint text-2xl group-hover:translate-x-1 transition-transform duration-500">→</span>
      </div>
    </Link>
  );
}
